import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { PaymentPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { PaymentTransfersDataTable } from 'modules/banking/components/PaymentTransfersDataTable'
import { useMarkAsPaidPaymentExecution } from 'modules/banking/hooks/useMarkAsPaidPaymentExecution'
import { usePayBankingPayment } from 'modules/banking/hooks/usePayBankingPayment'
import { useRevokeTransfersMutation } from 'modules/banking/hooks/useRevokeTransfers'
import { useUpdateSepaFile } from 'modules/banking/hooks/useUpdateSepaFile'
import {
  lastPaymentExecutionfromPaymentDetail,
  supportsSepaPayment,
} from 'modules/banking/lib/paymentHelpers'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const PaymentPage = () => {
  const { id } = useParams()
  const { company, employee } = useCurrentSession()
  const { updateSepaFile, processDownload } = useUpdateSepaFile()
  const { markAsPaidPaymentExecution } = useMarkAsPaidPaymentExecution()
  const { payPayment } = usePayBankingPayment()

  const { submitRevokeTransfers } = useRevokeTransfersMutation()

  const { data, loading, error, refetch } = useQuery(PaymentPageDocument, {
    variables: {
      id: Number(id),
      companyId: company.id,
      featureNames: ['dev_finance_swan_reactivation'],
    },
  })

  const canSeeSepaTransferAction =
    data?.features?.featuresConnection?.nodes.find(
      (feature) => feature.name === 'dev_finance_swan_reactivation'
    )?.enabled ?? false

  const canSeePaymentsModule =
    !!data?.finance?._policies?.sidebarSeeFinancePaymentsSubLevelItem?.can

  const payment = data?.banking?.payment
  if (!payment) return <></>

  const paymentExecution = lastPaymentExecutionfromPaymentDetail(payment)
  if (!paymentExecution) return <></>

  const legalEntitiesOptions =
    data?.companies.legalEntities.flatMap((p) => {
      return { label: p.legalName, value: p.id }
    }) || []

  const requestersOptions = paymentExecution.transfers
    .flatMap((transfer) => {
      return transfer.requester
        ? [{ label: transfer.requester.access.fullName, value: transfer.requester.id }]
        : []
    })
    .filter((option, index, self) => index === self.findIndex((o) => o.value === option.value))

  const supportsSepa = supportsSepaPayment(
    payment.bank_account?.account?.providerConfiguration?.services
  )

  const payWithSepaTransferAction = async () => {
    const employeeId = employee?.id
    if (!employeeId) return

    const redirectUrl = window.location.href
    const result = await payPayment(Number(id), employeeId, redirectUrl)

    if (result.authorizationUrl) {
      window.location.href = result.authorizationUrl
    } else if (result.success && result.consentUrl) {
      window.location.href = result.consentUrl
    }
  }

  const downloadSepaAction = () => {
    if (paymentExecution.sepaFile?.url) {
      processDownload(paymentExecution.sepaFile.url, paymentExecution.sepaFile.filename)
    } else {
      updateSepaFile(paymentExecution.id)
    }
  }

  const handleSubmitRevokeTransfer = async (transferId: number) => {
    await submitRevokeTransfers({
      transferIds: [transferId],
      paymentId: Number(id),
    })
    refetch({ id: Number(id) })
  }

  return (
    <ResponsivePage.Body
      error={error}
      loading={loading}
      authorized={canSeePaymentsModule}
      variant="full"
      onActionClick={async (key) => {
        if (key === 'pay_with_sepa_transfer') {
          await payWithSepaTransferAction()
        } else if (key === 'download_sepa_file') {
          downloadSepaAction()
        } else if (key === 'mark_as_paid') {
          markAsPaidPaymentExecution(paymentExecution.id)
        }
      }}
    >
      <Box flexDirection="column" gap="s32">
        {paymentExecution && (
          <PaymentTransfersDataTable
            paymentExecution={paymentExecution}
            onRevokeTransfer={handleSubmitRevokeTransfer}
            legalEntitiesOptions={legalEntitiesOptions}
            requestersOptions={requestersOptions}
            seeStatusColumn={supportsSepa && canSeeSepaTransferAction}
          />
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default PaymentPage
